import type { Load, LoadItem } from "@shared/schema";
import { Badge } from "@/components/ui/badge";

type NullablePartialLoad = {
  [Key in keyof Load]?:
    | Load[Key]
    | (Extract<Load[Key], Date> extends never ? never : string)
    | null;
};

type ShipmentLoadDetailsRecord = NullablePartialLoad & {
  items?: LoadItem[] | null;
};

function hasValue(value: unknown): boolean {
  return value !== null && value !== undefined && value !== "";
}

function formatDate(value?: Date | string | null): string | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString();
}

function formatTime(value?: string | null): string | null {
  if (!value) return null;
  const match = value.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return value;

  const hour = Number(match[1]);
  const minute = match[2];
  const period = hour >= 12 ? "PM" : "AM";
  return `${hour % 12 || 12}:${minute} ${period}`;
}

function formatTimeWindow(start?: string | null, end?: string | null): string | null {
  const formattedStart = formatTime(start);
  const formattedEnd = formatTime(end);
  if (formattedStart && formattedEnd) return `${formattedStart} – ${formattedEnd}`;
  if (formattedStart) return `From ${formattedStart}`;
  if (formattedEnd) return `Until ${formattedEnd}`;
  return null;
}

function formatDimensions(
  length?: number | null,
  width?: number | null,
  height?: number | null,
): string | null {
  if (![length, width, height].some(hasValue)) return null;
  return [length, width, height]
    .map((value) => (hasValue(value) ? `${value}″` : "—"))
    .join(" × ");
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (!hasValue(value)) return null;
  return (
    <div className="grid grid-cols-[minmax(7rem,0.8fr)_minmax(0,1.2fr)] gap-3">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium break-words text-right">{value}</dd>
    </div>
  );
}

function DetailSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      <dl className="space-y-1.5">{children}</dl>
    </section>
  );
}

function ScheduleSection({
  title,
  timeStart,
  timeEnd,
  address,
  contactName,
  contactPhone,
}: {
  title: string;
  timeStart?: string | null;
  timeEnd?: string | null;
  address?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
}) {
  const timeWindow = formatTimeWindow(timeStart, timeEnd);
  if (![timeWindow, address, contactName, contactPhone].some(hasValue)) return null;

  return (
    <DetailSection title={title}>
      <DetailRow label="Time" value={timeWindow} />
      <DetailRow label="Address" value={address} />
      <DetailRow label="Contact" value={contactName} />
      <DetailRow label="Phone" value={contactPhone} />
    </DetailSection>
  );
}

export default function ShipmentLoadDetails({ load }: { load: ShipmentLoadDetailsRecord }) {
  const pickupDate = formatDate(load.pickupDate);
  const deliveryDate = formatDate(load.deliveryDate);
  const loadDimensions = formatDimensions(load.length, load.width, load.height);
  const services = [
    load.liftgatePickup && "Liftgate at pickup",
    load.insidePickup && "Inside pickup",
    load.liftgateDelivery && "Liftgate at delivery",
    load.insideDelivery && "Inside delivery",
    load.callBeforeDelivery && "Call before delivery",
  ].filter((service): service is string => Boolean(service));
  const items = load.items ?? [];
  const hasRouteAndDates = [
    load.origin,
    load.destination,
    pickupDate,
    deliveryDate,
    load.pickupLocationType,
    load.deliveryLocationType,
  ].some(hasValue);
  const hasFreightSummary = [
    load.weight,
    load.dimensions,
    load.handlingUnit,
    load.quantity,
    loadDimensions,
    load.stackable ? "stackable" : null,
    load.hazardous ? "hazardous" : null,
  ].some(hasValue);
  const hasScheduling =
    [
      load.pickupTimeStart,
      load.pickupTimeEnd,
      load.pickupAddress,
      load.pickupContactName,
      load.pickupContactPhone,
      load.deliveryTimeStart,
      load.deliveryTimeEnd,
      load.deliveryAddress,
      load.deliveryContactName,
      load.deliveryContactPhone,
    ].some(hasValue);

  return (
    <div className="space-y-5" data-testid="shipment-load-details">
      {hasRouteAndDates && (
        <DetailSection title="Route & schedule">
          <DetailRow label="Origin" value={load.origin} />
          <DetailRow label="Destination" value={load.destination} />
          <DetailRow label="Pickup date" value={pickupDate} />
          <DetailRow label="Delivery date" value={deliveryDate} />
          <DetailRow label="Pickup location" value={load.pickupLocationType} />
          <DetailRow label="Delivery location" value={load.deliveryLocationType} />
        </DetailSection>
      )}

      {hasFreightSummary && (
        <DetailSection title="Freight summary">
          <DetailRow
            label="Total weight"
            value={hasValue(load.weight) ? `${Number(load.weight).toLocaleString()} lbs` : null}
          />
          <DetailRow label="Recorded dimensions" value={load.dimensions} />
          <DetailRow label="Handling unit" value={load.handlingUnit} />
          <DetailRow label="Quantity" value={load.quantity} />
          <DetailRow label="L × W × H" value={loadDimensions} />
          {(load.stackable || load.hazardous) && (
            <div className="flex flex-wrap justify-end gap-2 pt-1">
              {load.stackable && <Badge variant="secondary">Stackable</Badge>}
              {load.hazardous && <Badge variant="secondary">Hazardous materials</Badge>}
            </div>
          )}
        </DetailSection>
      )}

      {items.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-foreground">Freight items</h3>
          <div className="space-y-3">
            {items.map((item, index) => {
              const dimensions = formatDimensions(item.length, item.width, item.height);
              return (
                <div
                  key={item.id ?? index}
                  className="space-y-1.5 border-l-2 border-border pl-3"
                  data-testid={`shipment-freight-item-${index}`}
                >
                  <p className="text-sm font-semibold">Item {index + 1}</p>
                  <dl className="space-y-1.5">
                    <DetailRow label="Handling unit" value={item.handlingUnit} />
                    <DetailRow label="Quantity" value={item.quantity} />
                    <DetailRow label="Dimensions" value={dimensions} />
                    <DetailRow
                      label="Weight"
                      value={hasValue(item.weight) ? `${Number(item.weight).toLocaleString()} lbs` : null}
                    />
                  </dl>
                  {(item.stackable || item.hazardous) && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {item.stackable && <Badge variant="secondary">Stackable</Badge>}
                      {item.hazardous && <Badge variant="secondary">Hazardous materials</Badge>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {services.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-foreground">Services</h3>
          <div className="flex flex-wrap gap-2">
            {services.map((service) => (
              <Badge key={service} variant="secondary">{service}</Badge>
            ))}
          </div>
        </section>
      )}

      {hasScheduling && (
        <section className="space-y-4 border-t pt-4">
          <h3 className="text-sm font-semibold text-foreground">Pickup & delivery details</h3>
          <ScheduleSection
            title="Pickup"
            timeStart={load.pickupTimeStart}
            timeEnd={load.pickupTimeEnd}
            address={load.pickupAddress}
            contactName={load.pickupContactName}
            contactPhone={load.pickupContactPhone}
          />
          <ScheduleSection
            title="Delivery"
            timeStart={load.deliveryTimeStart}
            timeEnd={load.deliveryTimeEnd}
            address={load.deliveryAddress}
            contactName={load.deliveryContactName}
            contactPhone={load.deliveryContactPhone}
          />
        </section>
      )}

      {hasValue(load.details) && (
        <DetailSection title="Notes">
          <dd className="text-sm font-medium whitespace-pre-line break-words">{load.details}</dd>
        </DetailSection>
      )}
    </div>
  );
}